import { QRCodeSVG } from 'qrcode.react';
import { Box, Button, Stack, Typography } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { PRODUCT, type ProductCode } from '../../branding';
import { useRoleProfile } from '../../hooks/useRoleProfile';
import { STORE_APPS, appBrandsFor, appPageUrl } from './appStore';

/** Apple logo — standard App Store brand mark. */
function AppleMark() {
  return (
    <svg viewBox="0 0 384 512" width="1em" height="1em" aria-hidden focusable="false">
      <path
        fill="currentColor"
        d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zm-56.6-164.2c27.3-32.4 24.8-61.9 24-72.5-24.1 1.4-52 16.4-67.9 34.9-17.5 19.8-27.8 44.3-25.6 71.9 26.1 2 49.9-11.4 69.5-34.3z"
      />
    </svg>
  );
}

/** Google Play — standard 4-colour play-triangle brand mark. */
function GooglePlayMark() {
  return (
    <svg viewBox="0 0 24 24" width="1em" height="1em" aria-hidden focusable="false">
      <path d="M3.609 1.814 13.792 12 3.61 22.186a1.5 1.5 0 0 1-.609-1.21V3.024c0-.474.21-.898.608-1.21z" fill="#00D2FF" />
      <path d="M16.806 9.812 5.864 2.336 3.61 1.814 13.792 12z" fill="#FF3A44" />
      <path d="M13.792 12 3.61 22.186l2.254-.522 10.942-7.476z" fill="#00CE5C" />
      <path d="M16.806 9.812 13.792 12l3.014 2.188 3.43-1.964c.6-.344.6-1.064 0-1.408z" fill="#FFC400" />
    </svg>
  );
}

const storeButtonSx = { textTransform: 'none', borderRadius: 1, color: 'text.primary', borderColor: 'divider' } as const;

/** One app: QR + two store buttons, or a "coming soon" line while the listing is not public. */
export function AppDownloadRow({ brand, qrSize = 120, subtitle }: { brand: ProductCode; qrSize?: number; subtitle?: string }) {
  const { t } = useTranslation();
  const app = STORE_APPS[brand];
  return (
    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2.5} alignItems="center">
      {app.live && (
        <Box sx={{ p: 1, borderRadius: 2, bgcolor: '#fff', border: '1px solid', borderColor: 'divider', lineHeight: 0 }}>
          <QRCodeSVG value={appPageUrl(brand)} size={qrSize} includeMargin={false} />
        </Box>
      )}
      <Box sx={{ flex: 1, minWidth: 0, textAlign: { xs: 'center', sm: 'start' } }}>
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          {t('worker:getApp.title', { product: app.name })}
        </Typography>
        {subtitle && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            {subtitle}
          </Typography>
        )}
        {app.live ? (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} sx={{ mt: 1.5, alignItems: { xs: 'stretch', sm: 'center' } }}>
              {app.appStoreLive && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Box component="span" sx={{ display: 'inline-flex', fontSize: 18, color: 'text.primary' }}><AppleMark /></Box>}
                  href={app.appStore}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={storeButtonSx}
                >
                  {t('worker:getApp.appStore')}
                </Button>
              )}
              {app.googlePlayLive && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Box component="span" sx={{ display: 'inline-flex', fontSize: 18 }}><GooglePlayMark /></Box>}
                  href={app.googlePlay}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={storeButtonSx}
                >
                  {t('worker:getApp.googlePlay')}
                </Button>
              )}
            </Stack>
            {!app.appStoreLive && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {t('worker:getApp.comingSoonAppStore')}
              </Typography>
            )}
            {!app.googlePlayLive && (
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.75 }}>
                {t('worker:getApp.comingSoonGooglePlay')}
              </Typography>
            )}
          </>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {t('worker:getApp.comingSoon')}
          </Typography>
        )}
      </Box>
    </Stack>
  );
}

export interface GetAppCardProps {
  /** Compact = smaller QR, no card chrome; used inside a host card or dialog. */
  compact?: boolean;
  /** Which apps to show. Default: the apps this organization is licensed for. */
  brands?: ProductCode[];
  /** Drop apps whose store listing is not public yet (a worker surface has no use for "coming soon"). */
  liveOnly?: boolean;
  /** Line under each app name. Default: the face-setup instruction this card was built for. */
  subtitle?: string;
}

/**
 * QR + store buttons for the organization's apps.
 *
 * The QR encodes `svdy.com/app/?p=<brand>` (see appStore.ts), which opens the right
 * store on iPhone and Android. A bundle organization gets both apps, each with its own
 * code, instead of one brand's links under a combined name.
 */
export default function GetAppCard({ compact = false, brands, liveOnly = false, subtitle }: GetAppCardProps) {
  const { t } = useTranslation();
  const { role } = useRoleProfile();
  const resolved = (brands ?? appBrandsFor(role?.licensedProducts, PRODUCT))
    .filter((b) => !liveOnly || STORE_APPS[b].live);
  if (resolved.length === 0) return null;
  const qrSize = compact ? 120 : 148;
  const line = subtitle ?? t('worker:getApp.subtitle');

  return (
    <Box
      sx={{
        width: '100%',
        // `compact` means "embedded inside another card" (the worker dashboard
        // and the liveness dialog). Keeping a border there stacked THREE
        // competing edges — host card, this box, and the QR frame — and pushed
        // this content to a third left offset, so the row read as a card inside
        // a card (owner review 2026-07-29). Embedded: no chrome, content aligns
        // to the host's inner edge. Standalone (the face-setup page): it is the
        // card, so it keeps its own.
        ...(compact
          ? {}
          : {
              p: 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }),
      }}
    >
      <Stack spacing={2.5}>
        {resolved.map((brand) => (
          <AppDownloadRow key={brand} brand={brand} qrSize={qrSize} subtitle={line} />
        ))}
      </Stack>
    </Box>
  );
}
